import React, { ReactElement, useRef } from 'react';
import { Text, Box, Button, VStack, HStack } from '@chakra-ui/react';
import { NovaIcon } from 'components/ui/novaIcon';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import { AddIcon, DeleteIcon } from 'assets/icons/v2';
import { MultipleChoiceBlockProps } from '../MultipleChoiceBlock';
import { TeacherMCOption } from './TeacherMCOption';
import { BlockInput } from '../../../common/BlockInput';

type Props = MultipleChoiceBlockProps & {
  onDelete?: () => void;
};

export type MCOption = {
  text: string;
  isChecked: boolean;
  isCorrect: boolean;
};

export const TeacherMCBlock = ({ block, editor, onDelete }: Props): ReactElement => {
  const [options, setOptions] = React.useState<MCOption[]>(
    JSON.parse(block.props.options) as MCOption[]
  );
  const optionRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [question, setQuestion] = React.useState<string>(block.props.question || '');
  const questionRef = React.useRef<HTMLTextAreaElement>(null);

  const onQuestionChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setQuestion(e.target.value);
    editor.updateBlock(block, {
      props: {
        ...block.props,
        question: e.target.value,
      },
    });
  };

  const updateOptions = (newOptions: MCOption[], focusIndex?: number): void => {
    editor.updateBlock(block, {
      props: {
        options: JSON.stringify(newOptions),
      },
    });

    setOptions(newOptions);

    if (typeof focusIndex === 'number') {
      setTimeout(() => {
        optionRefs.current[focusIndex]?.focus();
      }, 0);
    }
  };

  const toggleOption = (index: number): void => {
    const updated = options.map((opt, i) =>
      i === index ? { ...opt, isCorrect: !opt.isCorrect } : opt
    );
    editor.updateBlock(block, {
      props: {
        options: JSON.stringify(updated),
      },
    });
    setOptions(updated);
  };

  const updateOptionText = (index: number, text: string): void => {
    const updated = options.map((opt, i) => (i === index ? { ...opt, text } : opt));
    updateOptions(updated);
  };

  const deleteFocusedOption = (index: number): void => {
    const updated = options.filter((_, i) => i !== index);
    const newFocusIndex = index > 0 ? index - 1 : 0;

    updateOptions(updated, newFocusIndex);
  };

  const addOptionToEnd = (newOption?: Partial<MCOption>): void => {
    const updatedOptions = [
      ...options,
      {
        text: newOption?.text ?? '',
        isCorrect: newOption?.isCorrect ?? false,
        isChecked: newOption?.isChecked ?? false,
      },
    ];

    updateOptions(updatedOptions, updatedOptions.length - 1);
  };

  return (
    <VStack
      border="default"
      borderColor="border.accent"
      borderRadius="l"
      p="l"
      gap="l"
      width="100%"
      alignItems="flex-start"
      contentEditable={false}
      bg="grey.white"
    >
      <HStack justifyContent="space-between" alignItems="flex-start" width="100%">
        <Text textStyle="h4.medium" color="text.primary">
          Multiple Choice Frage
        </Text>
        {onDelete && (
          <NovaIconButton
            aria-label="Frage löschen"
            variant="primary"
            size="m"
            onClick={onDelete}
            _hover={{
              '& svg': {
                color: 'red.500',
              },
            }}
            boxSize="18px"
            fontSize="18px"
            lineHeight="0"
            minWidth="0px"
            icon={DeleteIcon}
            iconProps={{ fill: 'transparent', color: 'icon.quaternary' }}
          />
        )}
      </HStack>
      <Box display="flex" flexDirection="column" gap="xs" width="100%">
        <BlockInput
          label="Frage"
          name="question"
          value={question}
          onChange={onQuestionChange}
          placeholder="Gib deine Frage hier ein..."
          ref={questionRef}
        />
      </Box>

      <Box display="flex" flexDirection="column" gap="xs" width="100%">
        <Text mb={0} textStyle="body.s.regular" color="text.primary">
          Antworten
        </Text>
        {options.map((opt, i) => (
          <TeacherMCOption
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            opt={opt}
            i={i}
            optionRefs={optionRefs}
            toggleOption={toggleOption}
            updateOptionText={updateOptionText}
            updateOptions={updateOptions}
            deleteFocusedOption={deleteFocusedOption}
            editorContext={{
              options,
              editor,
              block,
            }}
          />
        ))}
      </Box>
      <Button
        onClick={() => addOptionToEnd()}
        variant="tertiaryAction"
        alignSelf="flex-start"
        p={0}
        textStyle="body.s.regular"
      >
        <NovaIcon icon={AddIcon} size="m" aria-hidden />
        Option hinzufügen
      </Button>
    </VStack>
  );
};
